import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Autocomplete, FormControlLabel, Switch, Tabs, Tab,
  Menu, ListItemIcon, ListItemText, Rating, Avatar, LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Print as PrintIcon,
  Person as PersonIcon, Business as BusinessIcon,
  CheckCircle as ApproveIcon, Cancel as RejectIcon, Send as SendIcon,
  ContentCopy as DuplicateIcon, MoreVert as MoreIcon,
  AttachMoney as MoneyIcon, CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon, Visibility as ViewIcon,
  RemoveCircleOutline as RemoveIcon, AddCircleOutline as AddItemIcon,
  LocalShipping as ShippingIcon, Science as LabIcon,
  Assignment as OrderIcon, Warning as WarningIcon,
  Settings as SettingsIcon, Build as BuildIcon,
  Payment as PaymentIcon, History as HistoryIcon,
  AccessTime as TimeIcon, Star as StarIcon,
  RotateLeft as ReworkIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';


interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
function TabPanel({ children, value, index }: TabPanelProps) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

// Status labels and colors
const statusLabels: Record<string, string> = {
  draft: 'Rascunho', sent: 'Enviado', in_production: 'Em Produção', ready: 'Pronto',
  shipped_back: 'Em Trânsito', received: 'Recebido', approved: 'Aprovado',
  rejected: 'Rejeitado', rework: 'Retrabalho', completed: 'Concluído', cancelled: 'Cancelado',
};
const statusColors: Record<string, string> = {
  draft: '#6b7280', sent: '#3b82f6', in_production: '#f59e0b', ready: '#8b5cf6',
  shipped_back: '#06b6d4', received: '#10b981', approved: '#14b8a6',
  rejected: '#ef4444', rework: '#f97316', completed: '#059669', cancelled: '#9ca3af',
};
const priorityLabels: Record<string, string> = { low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };
const priorityColors: Record<string, string> = { low: '#6b7280', normal: '#3b82f6', high: '#f59e0b', urgent: '#ef4444' };
const paymentStatusLabels: Record<string, string> = { pending: 'Pendente', partial: 'Parcial', paid: 'Pago', overdue: 'Em Atraso' };
const paymentStatusColors: Record<string, string> = { pending: '#f59e0b', partial: '#3b82f6', paid: '#10b981', overdue: '#ef4444' };

const shadeGuides = ['VITA Classical', 'VITA 3D Master', 'Chromascop', 'Outro'];
const deliveryMethods = ['Motoboy', 'Correios', 'Transportadora', 'Retirada no Lab', 'Entrega pelo Lab'];
const moldTypes = ['Alginato', 'Silicone de Adição', 'Silicone de Condensação', 'Poliéter', 'Escaneamento Digital', 'Moldeira Aberta', 'Moldeira Fechada'];

const formatCurrency = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const formatDate = (d: string) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';

const emptyItem = {
  work_type_id: '', work_type_name: '', work_type_category: '',
  tooth_number: '', tooth_region: '', tooth_surface: '',
  material: '', shade: '', shade_details: '',
  specifications: '', special_instructions: '',
  unit_price: 0, quantity: 1, total_price: 0,
};

// Main tabs
type MainView = 'orders' | 'laboratories' | 'work-types';

const Laboratory: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();

  // === State ===
  const [mainView, setMainView] = useState<MainView>('orders');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Stats
  const [stats, setStats] = useState<any>({});

  // Orders list
  const [orders, setOrders] = useState<any[]>([]);
  const [orderSearch, setOrderSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  // Order dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Order form
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<any>(null);
  const [selectedLab, setSelectedLab] = useState<any>(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [professionalSearch, setProfessionalSearch] = useState('');
  const [labSearchForm, setLabSearchForm] = useState('');
  const [patientOptions, setPatientOptions] = useState<any[]>([]);
  const [professionalOptions, setProfessionalOptions] = useState<any[]>([]);
  const [labOptionsForm, setLabOptionsForm] = useState<any[]>([]);

  const [orderForm, setOrderForm] = useState({
    priority: 'normal', diagnosis: '', treatment_plan: '', dental_arch: '',
    tooth_notation: '', shade: '', shade_guide: '', shade_details: '',
    antagonist_info: '', occlusion_type: '', mold_type: '', mold_material: '',
    has_physical_mold: true, has_digital_scan: false,
    discount_amount: 0, additional_charges: 0, shipping_cost: 0,
    payment_method: '', payment_due_date: '',
    delivery_method: '', tracking_code: '', delivery_notes: '',
    clinical_notes: '', lab_notes: '', internal_notes: '',
    expected_return_date: '', order_date: new Date().toISOString().split('T')[0],
  });
  const [items, setItems] = useState<any[]>([{ ...emptyItem }]);

  // Work type search for items
  const [workTypeSearch, setWorkTypeSearch] = useState('');
  const [workTypeOptions, setWorkTypeOptions] = useState<any[]>([]);

  // View/Print
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // More options menu
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuOrder, setMenuOrder] = useState<any>(null);

  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({ status: '', notes: '' });

  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_method: '', reference: '', description: '' });

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [orderHistory, setOrderHistory] = useState<any[]>([]);

  // === LABORATORIES TAB ===
  const [laboratories, setLaboratories] = useState<any[]>([]);
  const [labSearch, setLabSearch] = useState('');
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [editingLabId, setEditingLabId] = useState<string | null>(null);
  const [labForm, setLabForm] = useState({
    name: '', trade_name: '', cnpj: '', registration_number: '',
    responsible_name: '', responsible_registration: '',
    phone: '', phone2: '', whatsapp: '', email: '', website: '',
    address_street: '', address_number: '', address_complement: '',
    address_neighborhood: '', address_city: '', address_state: '', address_zip: '',
    specialties: [] as string[], materials_offered: [] as string[],
    payment_terms: '', payment_method: '', bank_name: '', bank_agency: '',
    bank_account: '', pix_key: '', default_turnaround_days: 7, notes: '',
  });
  const [labFinancial, setLabFinancial] = useState<any>(null);
  const [labFinancialDialogOpen, setLabFinancialDialogOpen] = useState(false);

  // === WORK TYPES TAB ===
  const [workTypes, setWorkTypes] = useState<any[]>([]);
  const [wtSearch, setWtSearch] = useState('');
  const [wtDialogOpen, setWtDialogOpen] = useState(false);
  const [editingWtId, setEditingWtId] = useState<string | null>(null);
  const [wtForm, setWtForm] = useState({
    name: '', code: '', category: '', subcategory: '', material: '',
    description: '', default_price: 0, default_turnaround_days: 7,
    requires_shade: true, requires_mold: true, requires_photo: false, requires_scan: false,
  });

  // ==========================
  // FETCH DATA
  // ==========================

  const fetchStats = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/lab/stats'));
      if (res.data?.success !== false) setStats(res.data?.data || {});
    } catch (err) { console.error('Error fetching lab stats:', err); }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl('/clinic/lab-orders'), {
        params: { search: orderSearch || '', status: statusFilter || '', priority: priorityFilter || '', payment_status: paymentFilter || '', limit: 100 }
      });
      if (res.data?.success !== false) setOrders(res.data?.data || []);
    } catch (err) { console.error('Error fetching lab orders:', err); }
    setLoading(false);
  };

  const fetchLaboratories = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/laboratories'), { params: { search: labSearch || '', limit: 100 } });
      if (res.data?.success !== false) setLaboratories(res.data?.data || []);
    } catch (err) { console.error('Error fetching laboratories:', err); }
  };

  const fetchWorkTypes = async () => {
    try {
      const res = await axios.get(getApiUrl('/clinic/lab-work-types'), { params: { search: wtSearch || '' } });
      if (res.data?.success !== false) setWorkTypes(res.data?.data || []);
    } catch (err) { console.error('Error fetching work types:', err); }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchOrders(); }, [orderSearch, statusFilter, priorityFilter, paymentFilter]);
  useEffect(() => { fetchLaboratories(); }, [labSearch]);
  useEffect(() => { fetchWorkTypes(); }, [wtSearch]);

  // Search helpers
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/lab-search/patients'), { params: { search: patientSearch || '' } });
        setPatientOptions(res.data?.data || []);
      } catch (e) { setPatientOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [patientSearch]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/lab-search/professionals'), { params: { search: professionalSearch || '' } });
        setProfessionalOptions(res.data?.data || []);
      } catch (e) { setProfessionalOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [professionalSearch]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/lab-search/laboratories'), { params: { search: labSearchForm || '' } });
        setLabOptionsForm(res.data?.data || []);
      } catch (e) { setLabOptionsForm([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [labSearchForm]);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(getApiUrl('/clinic/lab-search/work-types'), { params: { search: workTypeSearch || '' } });
        setWorkTypeOptions(res.data?.data || []);
      } catch (e) { setWorkTypeOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [workTypeSearch]);

  useEffect(() => {
    if (dialogOpen) {
      setPatientSearch('');
      setProfessionalSearch('');
      setLabSearchForm('');
      setWorkTypeSearch('');
    }
  }, [dialogOpen]);

  // ==========================
  // ORDER ACTIONS
  // ==========================

  const resetOrderForm = () => {
    setSelectedPatient(null);
    setSelectedProfessional(null);
    setSelectedLab(null);
    setPatientSearch('');
    setProfessionalSearch('');
    setLabSearchForm('');
    setWorkTypeSearch('');
    setWorkTypeOptions([]);
    setPatientOptions([]);
    setProfessionalOptions([]);
    setLabOptionsForm([]);
    setOrderForm({
      priority: 'normal', diagnosis: '', treatment_plan: '', dental_arch: '',
      tooth_notation: '', shade: '', shade_guide: '', shade_details: '',
      antagonist_info: '', occlusion_type: '', mold_type: '', mold_material: '',
      has_physical_mold: true, has_digital_scan: false,
      discount_amount: 0, additional_charges: 0, shipping_cost: 0,
      payment_method: '', payment_due_date: '',
      delivery_method: '', tracking_code: '', delivery_notes: '',
      clinical_notes: '', lab_notes: '', internal_notes: '',
      expected_return_date: '', order_date: new Date().toISOString().split('T')[0],
    });
    setItems([{ ...emptyItem }]);
    setDialogTab(0);
  };

  const addItem = () => setItems([...items, { ...emptyItem }]);
  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    if (field === 'unit_price' || field === 'quantity') {
      newItems[idx].total_price = (newItems[idx].unit_price || 0) * (newItems[idx].quantity || 1);
    }
    setItems(newItems);
  };

  const selectWorkTypeForItem = (idx: number, wt: any) => {
    if (!wt) return;
    const newItems = [...items];
    newItems[idx] = {
      ...newItems[idx],
      work_type_id: wt.id, work_type_name: wt.name, work_type_category: wt.category || '',
      material: wt.material || '', unit_price: wt.default_price || 0,
      total_price: (wt.default_price || 0) * (newItems[idx].quantity || 1),
    };
    if (wt.requires_shade && !newItems[idx].shade) newItems[idx].shade = orderForm.shade || '';
    setItems(newItems);
  };

  const getSubtotal = () => items.reduce((s, i) => s + (i.total_price || 0), 0);
  const getTotal = () => getSubtotal() - (orderForm.discount_amount || 0) + (orderForm.additional_charges || 0) + (orderForm.shipping_cost || 0);

  const handleSaveOrder = async () => {
    if (!selectedLab) { setSnackbar({ open: true, message: 'Selecione um laboratório', severity: 'error' }); return; }
    if (!selectedPatient) { setSnackbar({ open: true, message: 'Selecione um paciente', severity: 'error' }); return; }
    if (items.every(i => !i.work_type_name)) { setSnackbar({ open: true, message: 'Adicione pelo menos uma peça/trabalho', severity: 'error' }); return; }

    setSaving(true);
    try {
      const payload = {
        ...orderForm,
        patient_id: selectedPatient?.id, patient_name: selectedPatient?.full_name,
        professional_id: selectedProfessional?.id || null, professional_name: selectedProfessional?.full_name || '',
        laboratory_id: selectedLab?.id, laboratory_name: selectedLab?.name,
        items: items.filter(i => i.work_type_name),
      };
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/lab-orders/${editingId}`), payload);
        setSnackbar({ open: true, message: 'Ordem de serviço atualizada!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/lab-orders'), payload);
        setSnackbar({ open: true, message: 'Ordem de serviço criada!', severity: 'success' });
      }
      setDialogOpen(false);
      resetOrderForm();
      fetchOrders();
      fetchStats();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erro: ${err.response?.data?.error || err.message}`, severity: 'error' });
    }
    setSaving(false);
  };

  const handleEditOrder = async (order: any) => {
    setEditingId(order.id);
    try {
      const res = await axios.get(getApiUrl(`/clinic/lab-orders/${order.id}`));
      const o = res.data?.data;
      if (!o) return;

      setSelectedPatient(o.patient_id ? { id: o.patient_id, full_name: o.patient_name, cpf: o.patient_cpf, phone: o.patient_phone } : null);
      setSelectedProfessional(o.professional_id ? { id: o.professional_id, full_name: o.professional_name, registration_number: o.professional_registration } : null);
      setSelectedLab(o.laboratory_id ? { id: o.laboratory_id, name: o.laboratory_name, phone: o.laboratory_phone } : null);

      setOrderForm({
        priority: o.priority || 'normal', diagnosis: o.diagnosis || '', treatment_plan: o.treatment_plan || '',
        dental_arch: o.dental_arch || '', tooth_notation: o.tooth_notation || '',
        shade: o.shade || '', shade_guide: o.shade_guide || '', shade_details: o.shade_details || '',
        antagonist_info: o.antagonist_info || '', occlusion_type: o.occlusion_type || '',
        mold_type: o.mold_type || '', mold_material: o.mold_material || '',
        has_physical_mold: o.has_physical_mold ?? true, has_digital_scan: o.has_digital_scan ?? false,
        discount_amount: o.discount_amount || 0, additional_charges: o.additional_charges || 0, shipping_cost: o.shipping_cost || 0,
        payment_method: o.payment_method || '', payment_due_date: o.payment_due_date || '',
        delivery_method: o.delivery_method || '', tracking_code: o.tracking_code || '', delivery_notes: o.delivery_notes || '',
        clinical_notes: o.clinical_notes || '', lab_notes: o.lab_notes || '', internal_notes: o.internal_notes || '',
        expected_return_date: o.expected_return_date || '', order_date: o.order_date || new Date().toISOString().split('T')[0],
      });

      if (o.items && o.items.length > 0) {
        setItems(o.items.map((it: any) => ({
          work_type_id: it.work_type_id || '', work_type_name: it.work_type_name || '',
          work_type_category: it.work_type_category || '',
          tooth_number: it.tooth_number || '', tooth_region: it.tooth_region || '',
          tooth_surface: it.tooth_surface || '',
          material: it.material || '', shade: it.shade || '', shade_details: it.shade_details || '',
          specifications: it.specifications || '', special_instructions: it.special_instructions || '',
          unit_price: it.unit_price || 0, quantity: it.quantity || 1, total_price: it.total_price || 0,
        })));
      } else {
        setItems([{ ...emptyItem }]);
      }

      setDialogTab(0);
      setDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao carregar ordem', severity: 'error' }); }
  };

  const handleDeleteOrder = async (id: string) => {
    if (!window.confirm('Deseja realmente excluir esta ordem de serviço?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/lab-orders/${id}`));
      setSnackbar({ open: true, message: 'Ordem excluída', severity: 'success' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleDuplicate = async (id: string) => {
    try {
      await axios.post(getApiUrl(`/clinic/lab-orders/${id}/duplicate`));
      setSnackbar({ open: true, message: 'Ordem duplicada!', severity: 'success' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao duplicar', severity: 'error' }); }
  };

  const handleStatusChange = async () => {
    if (!menuOrder?.id || !statusChangeData.status) return;
    try {
      await axios.patch(getApiUrl(`/clinic/lab-orders/${menuOrder.id}/status`), statusChangeData);
      setSnackbar({ open: true, message: 'Status atualizado!', severity: 'success' });
      setStatusDialogOpen(false); setStatusChangeData({ status: '', notes: '' }); setMenuOrder(null);
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao atualizar status', severity: 'error' }); }
  };

  const handleView = async (order: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/lab-orders/${order.id}`));
      setSelectedOrder(res.data?.data);
      setViewDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao carregar detalhes', severity: 'error' }); }
  };

  const handlePrint = async (order: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/lab-orders/${order.id}/print`));
      setSelectedOrder(res.data?.data);
      setPrintDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao preparar impressão', severity: 'error' }); }
  };

  const executePrint = () => {
    if (!printRef.current) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>Ordem de Serviço</title><style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 20px; color: #333; font-size: 11px; }
      .header { display: flex; justify-content: space-between; border-bottom: 2px solid #14b8a6; padding-bottom: 10px; margin-bottom: 15px; }
      .clinic-name { font-size: 16px; font-weight: bold; color: #0d9488; }
      .section { margin-bottom: 12px; }
      .section-title { font-weight: bold; color: #0d9488; font-size: 12px; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; margin-bottom: 6px; }
      .row { display: flex; gap: 15px; margin-bottom: 4px; }
      .label { font-weight: 600; min-width: 120px; }
      table { width: 100%; border-collapse: collapse; margin-top: 6px; }
      th, td { border: 1px solid #d1d5db; padding: 4px 6px; text-align: left; font-size: 10px; }
      th { background-color: #f0fdf4; color: #0d9488; font-weight: 600; }
      .total-row { font-weight: bold; background-color: #f0fdf4; }
      .notes { border: 1px solid #e5e7eb; padding: 8px; border-radius: 4px; min-height: 40px; margin-top: 4px; white-space: pre-wrap; }
      .signatures { display: flex; justify-content: space-between; margin-top: 30px; }
      .signature-line { text-align: center; width: 45%; }
      .signature-line hr { border: none; border-top: 1px solid #333; margin-bottom: 4px; }
      .prof-sig-print { text-align: center; margin-bottom: 6px; }
      .prof-sig-print img { max-height: 90px; max-width: 360px; object-fit: contain; display: inline-block; vertical-align: bottom; }
      .priority-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-weight: 600; font-size: 10px; }
      @media print { body { padding: 10px; } }
    </style></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 500);
  };

  // Payment
  const handleAddPayment = async () => {
    if (!menuOrder?.id || !paymentForm.amount) return;
    try {
      await axios.post(getApiUrl('/clinic/lab-payments'), {
        laboratory_id: menuOrder.laboratory_id,
        lab_order_id: menuOrder.id,
        ...paymentForm,
      });
      setSnackbar({ open: true, message: 'Pagamento registrado!', severity: 'success' });
      setPaymentDialogOpen(false);
      setPaymentForm({ amount: 0, payment_date: new Date().toISOString().split('T')[0], payment_method: '', reference: '', description: '' });
      fetchOrders(); fetchStats();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao registrar pagamento', severity: 'error' }); }
  };

  // History
  const handleViewHistory = async (order: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/lab-orders/${order.id}/history`));
      setOrderHistory(res.data?.data || []);
      setHistoryDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao carregar histórico', severity: 'error' }); }
  };

  // ==========================
  // LABORATORY ACTIONS
  // ==========================

  const resetLabForm = () => {
    setLabForm({
      name: '', trade_name: '', cnpj: '', registration_number: '',
      responsible_name: '', responsible_registration: '',
      phone: '', phone2: '', whatsapp: '', email: '', website: '',
      address_street: '', address_number: '', address_complement: '',
      address_neighborhood: '', address_city: '', address_state: '', address_zip: '',
      specialties: [], materials_offered: [],
      payment_terms: '', payment_method: '', bank_name: '', bank_agency: '',
      bank_account: '', pix_key: '', default_turnaround_days: 7, notes: '',
    });
  };

  const handleSaveLab = async () => {
    if (!labForm.name) { setSnackbar({ open: true, message: 'Nome do laboratório é obrigatório', severity: 'error' }); return; }
    setSaving(true);
    try {
      if (editingLabId) {
        await axios.put(getApiUrl(`/clinic/laboratories/${editingLabId}`), labForm);
        setSnackbar({ open: true, message: 'Laboratório atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/laboratories'), labForm);
        setSnackbar({ open: true, message: 'Laboratório cadastrado!', severity: 'success' });
      }
      setLabDialogOpen(false); resetLabForm(); setEditingLabId(null); fetchLaboratories();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erro: ${err.response?.data?.error || err.message}`, severity: 'error' });
    }
    setSaving(false);
  };

  const handleEditLab = (lab: any) => {
    setEditingLabId(lab.id);
    setLabForm({
      name: lab.name || '', trade_name: lab.trade_name || '', cnpj: lab.cnpj || '',
      registration_number: lab.registration_number || '',
      responsible_name: lab.responsible_name || '', responsible_registration: lab.responsible_registration || '',
      phone: lab.phone || '', phone2: lab.phone2 || '', whatsapp: lab.whatsapp || '',
      email: lab.email || '', website: lab.website || '',
      address_street: lab.address_street || '', address_number: lab.address_number || '',
      address_complement: lab.address_complement || '', address_neighborhood: lab.address_neighborhood || '',
      address_city: lab.address_city || '', address_state: lab.address_state || '', address_zip: lab.address_zip || '',
      specialties: lab.specialties || [], materials_offered: lab.materials_offered || [],
      payment_terms: lab.payment_terms || '', payment_method: lab.payment_method || '',
      bank_name: lab.bank_name || '', bank_agency: lab.bank_agency || '',
      bank_account: lab.bank_account || '', pix_key: lab.pix_key || '',
      default_turnaround_days: lab.default_turnaround_days || 7, notes: lab.notes || '',
    });
    setLabDialogOpen(true);
  };

  const handleDeleteLab = async (id: string) => {
    if (!window.confirm('Deseja realmente excluir este laboratório?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/laboratories/${id}`));
      setSnackbar({ open: true, message: 'Laboratório excluído', severity: 'success' });
      fetchLaboratories();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' }); }
  };

  const handleLabFinancial = async (lab: any) => {
    try {
      const res = await axios.get(getApiUrl(`/clinic/laboratories/${lab.id}/financial`));
      setLabFinancial({ ...res.data?.data, lab });
      setLabFinancialDialogOpen(true);
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao carregar financeiro', severity: 'error' }); }
  };

  // ==========================
  // WORK TYPE ACTIONS
  // ==========================

  const resetWtForm = () => {
    setWtForm({
      name: '', code: '', category: '', subcategory: '', material: '',
      description: '', default_price: 0, default_turnaround_days: 7,
      requires_shade: true, requires_mold: true, requires_photo: false, requires_scan: false,
    });
  };

  const handleSaveWt = async () => {
    if (!wtForm.name || !wtForm.category) { setSnackbar({ open: true, message: 'Nome e categoria são obrigatórios', severity: 'error' }); return; }
    setSaving(true);
    try {
      if (editingWtId) {
        await axios.put(getApiUrl(`/clinic/lab-work-types/${editingWtId}`), wtForm);
      } else {
        await axios.post(getApiUrl('/clinic/lab-work-types'), wtForm);
      }
      setSnackbar({ open: true, message: 'Tipo de trabalho salvo!', severity: 'success' });
      setWtDialogOpen(false); resetWtForm(); setEditingWtId(null); fetchWorkTypes();
    } catch (err: any) {
      setSnackbar({ open: true, message: `Erro: ${err.response?.data?.error || err.message}`, severity: 'error' });
    }
    setSaving(false);
  };

  const handleEditWt = (wt: any) => {
    setEditingWtId(wt.id);
    setWtForm({
      name: wt.name || '', code: wt.code || '', category: wt.category || '',
      subcategory: wt.subcategory || '', material: wt.material || '',
      description: wt.description || '', default_price: wt.default_price || 0,
      default_turnaround_days: wt.default_turnaround_days || 7,
      requires_shade: wt.requires_shade ?? true, requires_mold: wt.requires_mold ?? true,
      requires_photo: wt.requires_photo ?? false, requires_scan: wt.requires_scan ?? false,
    });
    setWtDialogOpen(true);
  };

  const handleDeleteWt = async (id: string) => {
    if (!window.confirm('Deseja realmente desativar este tipo de trabalho?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/lab-work-types/${id}`));
      setSnackbar({ open: true, message: 'Tipo de trabalho desativado', severity: 'success' });
      fetchWorkTypes();
    } catch (err) { setSnackbar({ open: true, message: 'Erro ao desativar', severity: 'error' }); }
  };

  // ==========================
  // RENDER
  // ==========================

  return (
    <Box sx={clinicPageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000' }}>Controle de Laboratório</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerencie laboratórios de prótese, ordens de serviço e pagamentos</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => { fetchOrders(); fetchLaboratories(); fetchWorkTypes(); fetchStats(); }}>
            <RefreshIcon sx={{ color: '#0d9488' }} />
          </IconButton>
          {mainView === 'orders' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingId(null); resetOrderForm(); setDialogOpen(true); }}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Nova Ordem de Serviço
            </Button>
          )}
          {mainView === 'laboratories' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingLabId(null); resetLabForm(); setLabDialogOpen(true); }}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Novo Laboratório
            </Button>
          )}
          {mainView === 'work-types' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingWtId(null); resetWtForm(); setWtDialogOpen(true); }}
              sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Novo Tipo de Trabalho
            </Button>
          )}
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Ordens em Andamento', value: stats.pending_orders || 0, icon: <OrderIcon />, color: '#3b82f6' },
          { label: 'Em Produção', value: stats.in_production || 0, icon: <BuildIcon />, color: '#f59e0b' },
          { label: 'Atrasadas', value: stats.overdue_orders || 0, icon: <WarningIcon />, color: '#ef4444' },
          { label: 'Concluídas (Mês)', value: stats.completed_month || 0, icon: <ApproveIcon />, color: '#10b981' },
          { label: 'Custo Mensal', value: formatCurrency(stats.total_month_cost || 0), icon: <MoneyIcon />, color: '#8b5cf6' },
          { label: 'A Pagar', value: formatCurrency(stats.total_pending_payment || 0), icon: <PaymentIcon />, color: '#ef4444' },
          { label: 'Retrabalhos', value: stats.rework_orders || 0, icon: <ReworkIcon />, color: '#f97316' },
          { label: 'Total Ordens', value: stats.total_orders || 0, icon: <LabIcon />, color: '#14b8a6' },
        ].map((kpi, idx) => (
          <Grid item xs={6} sm={3} key={idx}>
            <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>{kpi.label}</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
                      {kpi.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${kpi.color}15`, width: 36, height: 36 }}>
                    {React.cloneElement(kpi.icon, { sx: { fontSize: 18, color: kpi.color } })}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Navigation Tabs */}
      <Tabs value={mainView} onChange={(_, v) => setMainView(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 }, '& .Mui-selected': { color: '#0d9488' }, '& .MuiTabs-indicator': { backgroundColor: '#14b8a6' } }}>
        <Tab label="Ordens de Serviço" value="orders" icon={<OrderIcon />} iconPosition="start" />
        <Tab label="Laboratórios" value="laboratories" icon={<BusinessIcon />} iconPosition="start" />
        <Tab label="Tipos de Trabalho" value="work-types" icon={<BuildIcon />} iconPosition="start" />
      </Tabs>

      {/* ========================= */}
      {/* ORDERS VIEW */}
      {/* ========================= */}
      {mainView === 'orders' && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Buscar por paciente, OS, laboratório..." value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
              sx={{ minWidth: 260 }} />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Prioridade</InputLabel>
              <Select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} label="Prioridade">
                <MenuItem value="">Todas</MenuItem>
                {Object.entries(priorityLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Pagamento</InputLabel>
              <Select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} label="Pagamento">
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(paymentStatusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Orders Table */}
          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#14b8a6' }} /></Box> : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>OS</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Paciente</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Laboratório</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Profissional</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Peças</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Prioridade</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Pagamento</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Envio</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Prazo</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.length === 0 ? (
                    <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhuma ordem de serviço encontrada</TableCell></TableRow>
                  ) : orders.map((order) => {
                    const isOverdue = order.expected_return_date && new Date(order.expected_return_date) < new Date() && !['completed', 'cancelled', 'approved'].includes(order.status);
                    return (
                      <TableRow key={order.id} hover sx={{ '&:hover': { bgcolor: '#f0fdf4' }, ...(isOverdue ? { bgcolor: '#fef2f2' } : {}) }}>
                        <TableCell sx={{ fontWeight: 600, color: '#0d9488', fontSize: '0.8rem' }}>{order.order_number}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{order.patient_name}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{order.laboratory_name}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{order.professional_name || '-'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{order.items?.length || 0}</TableCell>
                        <TableCell>
                          <Chip label={statusLabels[order.status] || order.status} size="small"
                            sx={{ bgcolor: `${statusColors[order.status]}15`, color: statusColors[order.status], fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={priorityLabels[order.priority] || order.priority} size="small"
                            sx={{ bgcolor: `${priorityColors[order.priority]}15`, color: priorityColors[order.priority], fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{formatCurrency(order.total_cost)}</TableCell>
                        <TableCell>
                          <Chip label={paymentStatusLabels[order.payment_status] || order.payment_status} size="small"
                            sx={{ bgcolor: `${paymentStatusColors[order.payment_status]}15`, color: paymentStatusColors[order.payment_status], fontWeight: 600, fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(order.sent_date)}</TableCell>
                        <TableCell sx={{ fontSize: '0.75rem', color: isOverdue ? '#ef4444' : '#475569', fontWeight: isOverdue ? 700 : 400 }}>
                          {formatDate(order.expected_return_date)} {isOverdue && '⚠️'}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Visualizar"><IconButton size="small" onClick={() => handleView(order)}><ViewIcon sx={{ fontSize: 16, color: '#64748b' }} /></IconButton></Tooltip>
                            <Tooltip title="Imprimir"><IconButton size="small" onClick={() => handlePrint(order)}><PrintIcon sx={{ fontSize: 16, color: '#64748b' }} /></IconButton></Tooltip>
                            <Tooltip title="Mais"><IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuOrder(order); }}><MoreIcon sx={{ fontSize: 16, color: '#64748b' }} /></IconButton></Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ========================= */}
      {/* LABORATORIES VIEW */}
      {/* ========================= */}
      {mainView === 'laboratories' && (
        <Box>
          <TextField size="small" placeholder="Buscar laboratório..." value={labSearch} onChange={(e) => setLabSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
            sx={{ mb: 2, minWidth: 300 }} />
          <Grid container spacing={2}>
            {laboratories.map((lab) => (
              <Grid item xs={12} md={6} lg={4} key={lab.id}>
                <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', '&:hover': { borderColor: '#99f6e4', boxShadow: '0 4px 12px rgba(20,184,166,0.1)' }, transition: 'all 0.2s' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{lab.name}</Typography>
                        {lab.trade_name && <Typography variant="caption" sx={{ color: '#64748b' }}>{lab.trade_name}</Typography>}
                      </Box>
                      <Chip label={lab.is_active ? 'Ativo' : 'Inativo'} size="small"
                        sx={{ bgcolor: lab.is_active ? '#dcfce7' : '#fee2e2', color: lab.is_active ? '#16a34a' : '#dc2626', fontSize: '0.65rem', fontWeight: 600 }} />
                    </Box>
                    {lab.responsible_name && (
                      <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5 }}>
                        TPD: {lab.responsible_name} {lab.responsible_registration && `(${lab.responsible_registration})`}
                      </Typography>
                    )}
                    {lab.phone && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Tel: {lab.phone}</Typography>}
                    {lab.email && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>Email: {lab.email}</Typography>}
                    {lab.address_city && <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>{lab.address_city}{lab.address_state ? ` - ${lab.address_state}` : ''}</Typography>}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Rating value={lab.quality_rating || 0} readOnly size="small" precision={0.5} />
                      <Typography variant="caption" sx={{ color: '#64748b' }}>({lab.total_orders || 0} ordens)</Typography>
                    </Box>

                    {lab.specialties && lab.specialties.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                        {lab.specialties.slice(0, 3).map((s: string, i: number) => (
                          <Chip key={i} label={s} size="small" sx={{ fontSize: '0.6rem', bgcolor: '#f0fdf4', color: '#0d9488' }} />
                        ))}
                        {lab.specialties.length > 3 && <Chip label={`+${lab.specialties.length - 3}`} size="small" sx={{ fontSize: '0.6rem' }} />}
                      </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#64748b' }}>Prazo: {lab.default_turnaround_days || 7} dias</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Financeiro"><IconButton size="small" onClick={() => handleLabFinancial(lab)}><MoneyIcon sx={{ fontSize: 16, color: '#0d9488' }} /></IconButton></Tooltip>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleEditLab(lab)}><EditIcon sx={{ fontSize: 16, color: '#64748b' }} /></IconButton></Tooltip>
                        <Tooltip title="Excluir"><IconButton size="small" onClick={() => handleDeleteLab(lab.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton></Tooltip>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {laboratories.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                  <BusinessIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                  <Typography>Nenhum laboratório cadastrado</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* ========================= */}
      {/* WORK TYPES VIEW */}
      {/* ========================= */}
      {mainView === 'work-types' && (
        <Box>
          <TextField size="small" placeholder="Buscar tipo de trabalho..." value={wtSearch} onChange={(e) => setWtSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} /></InputAdornment> }}
            sx={{ mb: 2, minWidth: 300 }} />
          <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Preço</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Prazo</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Requer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workTypes.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#94a3b8' }}>Nenhum tipo de trabalho encontrado</TableCell></TableRow>
                ) : workTypes.map((wt) => (
                  <TableRow key={wt.id} hover>
                    <TableCell sx={{ color: '#0d9488', fontWeight: 600, fontSize: '0.8rem' }}>{wt.code || '-'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{wt.name}</TableCell>
                    <TableCell><Chip label={wt.category} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#f0fdf4', color: '#0d9488' }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{wt.material || '-'}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{formatCurrency(wt.default_price)}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{wt.default_turnaround_days} dias</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {wt.requires_shade && <Chip label="Cor" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />}
                        {wt.requires_mold && <Chip label="Mold" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />}
                        {wt.requires_photo && <Chip label="Foto" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />}
                        {wt.requires_scan && <Chip label="Scan" size="small" sx={{ fontSize: '0.55rem', height: 18 }} />}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditWt(wt)}><EditIcon sx={{ fontSize: 16, color: '#64748b' }} /></IconButton>
                      <IconButton size="small" onClick={() => handleDeleteWt(wt.id)}><DeleteIcon sx={{ fontSize: 16, color: '#ef4444' }} /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ========================= */}
      {/* MORE OPTIONS MENU */}
      {/* ========================= */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuOrder(null); }}>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) handleEditOrder(menuOrder); }}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Editar</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) { setStatusChangeData({ status: '', notes: '' }); setStatusDialogOpen(true); } }}>
          <ListItemIcon><ShippingIcon fontSize="small" /></ListItemIcon><ListItemText>Alterar Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) { setPaymentForm({ amount: (menuOrder.total_cost || 0) - (menuOrder.paid_amount || 0), payment_date: new Date().toISOString().split('T')[0], payment_method: '', reference: '', description: '' }); setPaymentDialogOpen(true); } }}>
          <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon><ListItemText>Registrar Pagamento</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) handleViewHistory(menuOrder); }}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon><ListItemText>Histórico</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) handleDuplicate(menuOrder.id); }}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Duplicar</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setMenuAnchor(null); if (menuOrder) handleDeleteOrder(menuOrder.id); }} sx={{ color: '#ef4444' }}>
          <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon><ListItemText>Excluir</ListItemText>
        </MenuItem>
      </Menu>

      {/* ========================= */}
      {/* ORDER CREATE/EDIT DIALOG */}
      {/* ========================= */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f0fdf4' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingId ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}</Typography>
          <IconButton onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={dialogTab} onChange={(_, v) => setDialogTab(v)} sx={{ mb: 1, '& .Mui-selected': { color: '#0d9488' }, '& .MuiTabs-indicator': { backgroundColor: '#14b8a6' } }}>
            <Tab label="Paciente & Lab" />
            <Tab label="Peças / Trabalhos" />
            <Tab label="Clínico" />
            <Tab label="Financeiro & Entrega" />
            <Tab label="Observações" />
          </Tabs>

          {/* TAB 0: Patient & Lab */}
          <TabPanel value={dialogTab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Laboratório</Typography></Grid>
              <Grid item xs={12}>
                <Autocomplete options={labOptionsForm} getOptionLabel={(o: any) => `${o.name}${o.trade_name ? ` (${o.trade_name})` : ''}`}
                  value={selectedLab} onChange={(_, v) => setSelectedLab(v)} onInputChange={(_, v) => setLabSearchForm(v)}
                  onOpen={() => { if (labOptionsForm.length === 0) setLabSearchForm(''); }}
                  renderInput={(params) => <TextField {...params} label="Selecionar laboratório *" placeholder="Buscar..." fullWidth />}
                  noOptionsText="Nenhum laboratório encontrado" />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Paciente</Typography></Grid>
              <Grid item xs={12}>
                <Autocomplete options={patientOptions} getOptionLabel={(o: any) => `${o.full_name}${o.cpf ? ` - ${o.cpf}` : ''}${o.phone ? ` - ${o.phone}` : ''}`}
                  value={selectedPatient} onChange={(_, v) => setSelectedPatient(v)} onInputChange={(_, v) => setPatientSearch(v)}
                  onOpen={() => { if (patientOptions.length === 0) setPatientSearch(''); }}
                  renderInput={(params) => <TextField {...params} label="Selecionar paciente *" placeholder="Buscar..." fullWidth />}
                  noOptionsText="Nenhum paciente encontrado" />
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Profissional Solicitante</Typography></Grid>
              <Grid item xs={12}>
                <Autocomplete options={professionalOptions} getOptionLabel={(o: any) => `${o.full_name}${o.registration_number ? ` - ${o.registration_type || 'CRO'} ${o.registration_number}` : ''}`}
                  value={selectedProfessional} onChange={(_, v) => setSelectedProfessional(v)} onInputChange={(_, v) => setProfessionalSearch(v)}
                  onOpen={() => { if (professionalOptions.length === 0) setProfessionalSearch(''); }}
                  renderInput={(params) => <TextField {...params} label="Profissional solicitante" placeholder="Buscar..." fullWidth />}
                  noOptionsText="Nenhum profissional encontrado" />
              </Grid>

              <Grid item xs={6}>
                <TextField label="Data da Ordem" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                  value={orderForm.order_date} onChange={(e) => setOrderForm({ ...orderForm, order_date: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Prioridade</InputLabel>
                  <Select value={orderForm.priority} onChange={(e) => setOrderForm({ ...orderForm, priority: e.target.value })} label="Prioridade">
                    {Object.entries(priorityLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Prazo de Retorno" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
                  value={orderForm.expected_return_date} onChange={(e) => setOrderForm({ ...orderForm, expected_return_date: e.target.value })} />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 1: Items */}
          <TabPanel value={dialogTab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Peças / Trabalhos</Typography>
              <Button size="small" startIcon={<AddItemIcon />} onClick={addItem} sx={{ color: '#0d9488' }}>Adicionar Peça</Button>
            </Box>
            {items.map((item, index) => (
              <Card key={index} sx={{ mb: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Peça {index + 1}</Typography>
                  {items.length > 1 && <IconButton size="small" onClick={() => removeItem(index)} sx={{ color: '#ef4444' }}><RemoveIcon fontSize="small" /></IconButton>}
                </Box>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Autocomplete options={workTypeOptions}
                      getOptionLabel={(o: any) => `${o.name}${o.code ? ` (${o.code})` : ''} - ${o.category} - ${formatCurrency(o.default_price)}`}
                      onChange={(_, v) => selectWorkTypeForItem(index, v)}
                      onInputChange={(_, v) => setWorkTypeSearch(v)}
                      onOpen={() => { if (workTypeOptions.length === 0) setWorkTypeSearch(''); }}
                      renderInput={(params) => <TextField {...params} label="Buscar tipo de trabalho" placeholder="Coroa, Ponte, Faceta..." size="small" fullWidth />}
                      noOptionsText="Nenhum tipo encontrado" />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField label="Trabalho" value={item.work_type_name} onChange={(e) => updateItem(index, 'work_type_name', e.target.value)} size="small" fullWidth />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField label="Dente" value={item.tooth_number} onChange={(e) => updateItem(index, 'tooth_number', e.target.value)} size="small" fullWidth placeholder="11, 12..." />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Material" value={item.material} onChange={(e) => updateItem(index, 'material', e.target.value)} size="small" fullWidth />
                  </Grid>
                  <Grid item xs={3}>
                    <TextField label="Cor" value={item.shade} onChange={(e) => updateItem(index, 'shade', e.target.value)} size="small" fullWidth placeholder="A2, B1..." />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField label="Preço Unit." type="number" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value) || 0)} size="small" fullWidth />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField label="Qtd" type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)} size="small" fullWidth />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField label="Total" value={formatCurrency(item.total_price)} size="small" fullWidth InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Especificações" value={item.specifications} onChange={(e) => updateItem(index, 'specifications', e.target.value)} size="small" fullWidth multiline rows={1} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField label="Instruções especiais" value={item.special_instructions} onChange={(e) => updateItem(index, 'special_instructions', e.target.value)} size="small" fullWidth multiline rows={1} />
                  </Grid>
                </Grid>
              </Card>
            ))}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>Subtotal: {formatCurrency(getSubtotal())}</Typography>
            </Box>
          </TabPanel>

          {/* TAB 2: Clinical */}
          <TabPanel value={dialogTab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Informações Clínicas</Typography></Grid>
              <Grid item xs={6}>
                <TextField label="Notação Dentária" value={orderForm.tooth_notation} onChange={(e) => setOrderForm({ ...orderForm, tooth_notation: e.target.value })} size="small" fullWidth placeholder="11, 12, 13 ou Arcada completa" />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Arcada</InputLabel>
                  <Select value={orderForm.dental_arch} onChange={(e) => setOrderForm({ ...orderForm, dental_arch: e.target.value })} label="Arcada">
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="superior">Superior</MenuItem>
                    <MenuItem value="inferior">Inferior</MenuItem>
                    <MenuItem value="ambos">Ambas</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField label="Cor / Shade" value={orderForm.shade} onChange={(e) => setOrderForm({ ...orderForm, shade: e.target.value })} size="small" fullWidth placeholder="A2, A3, B1..." />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Escala de Cor</InputLabel>
                  <Select value={orderForm.shade_guide} onChange={(e) => setOrderForm({ ...orderForm, shade_guide: e.target.value })} label="Escala de Cor">
                    <MenuItem value="">-</MenuItem>
                    {shadeGuides.map(sg => <MenuItem key={sg} value={sg}>{sg}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField label="Detalhes de Cor" value={orderForm.shade_details} onChange={(e) => setOrderForm({ ...orderForm, shade_details: e.target.value })} size="small" fullWidth placeholder="Cervical A3, Corpo A2, Incisal translúcido" />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Diagnóstico" value={orderForm.diagnosis} onChange={(e) => setOrderForm({ ...orderForm, diagnosis: e.target.value })} size="small" fullWidth multiline rows={2} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Plano de Tratamento" value={orderForm.treatment_plan} onChange={(e) => setOrderForm({ ...orderForm, treatment_plan: e.target.value })} size="small" fullWidth multiline rows={2} />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Info Antagonista" value={orderForm.antagonist_info} onChange={(e) => setOrderForm({ ...orderForm, antagonist_info: e.target.value })} size="small" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <TextField label="Tipo de Oclusão" value={orderForm.occlusion_type} onChange={(e) => setOrderForm({ ...orderForm, occlusion_type: e.target.value })} size="small" fullWidth />
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Moldagem</Typography></Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de Moldagem</InputLabel>
                  <Select value={orderForm.mold_type} onChange={(e) => setOrderForm({ ...orderForm, mold_type: e.target.value })} label="Tipo de Moldagem">
                    <MenuItem value="">-</MenuItem>
                    {moldTypes.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField label="Material da Moldagem" value={orderForm.mold_material} onChange={(e) => setOrderForm({ ...orderForm, mold_material: e.target.value })} size="small" fullWidth />
              </Grid>
              <Grid item xs={4} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControlLabel control={<Switch checked={orderForm.has_physical_mold} onChange={(e) => setOrderForm({ ...orderForm, has_physical_mold: e.target.checked })} color="primary" />} label="Molde Físico" />
                <FormControlLabel control={<Switch checked={orderForm.has_digital_scan} onChange={(e) => setOrderForm({ ...orderForm, has_digital_scan: e.target.checked })} color="primary" />} label="Scan Digital" />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 3: Financial & Delivery */}
          <TabPanel value={dialogTab} index={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Financeiro</Typography></Grid>
              <Grid item xs={3}>
                <TextField label="Subtotal" value={formatCurrency(getSubtotal())} size="small" fullWidth InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Desconto (R$)" type="number" value={orderForm.discount_amount} onChange={(e) => setOrderForm({ ...orderForm, discount_amount: parseFloat(e.target.value) || 0 })} size="small" fullWidth />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Taxas Adicionais" type="number" value={orderForm.additional_charges} onChange={(e) => setOrderForm({ ...orderForm, additional_charges: parseFloat(e.target.value) || 0 })} size="small" fullWidth />
              </Grid>
              <Grid item xs={3}>
                <TextField label="Frete" type="number" value={orderForm.shipping_cost} onChange={(e) => setOrderForm({ ...orderForm, shipping_cost: parseFloat(e.target.value) || 0 })} size="small" fullWidth />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Total" value={formatCurrency(getTotal())} size="small" fullWidth InputProps={{ readOnly: true }} sx={{ '& .MuiInputBase-input': { fontWeight: 700, color: '#0d9488' } }} />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Forma de Pagamento" value={orderForm.payment_method} onChange={(e) => setOrderForm({ ...orderForm, payment_method: e.target.value })} size="small" fullWidth placeholder="PIX, Boleto..." />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Vencimento" type="date" value={orderForm.payment_due_date} onChange={(e) => setOrderForm({ ...orderForm, payment_due_date: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Entrega</Typography></Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Método de Entrega</InputLabel>
                  <Select value={orderForm.delivery_method} onChange={(e) => setOrderForm({ ...orderForm, delivery_method: e.target.value })} label="Método de Entrega">
                    <MenuItem value="">-</MenuItem>
                    {deliveryMethods.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <TextField label="Código de Rastreio" value={orderForm.tracking_code} onChange={(e) => setOrderForm({ ...orderForm, tracking_code: e.target.value })} size="small" fullWidth />
              </Grid>
              <Grid item xs={4}>
                <TextField label="Obs. de Entrega" value={orderForm.delivery_notes} onChange={(e) => setOrderForm({ ...orderForm, delivery_notes: e.target.value })} size="small" fullWidth />
              </Grid>
            </Grid>
          </TabPanel>

          {/* TAB 4: Notes */}
          <TabPanel value={dialogTab} index={4}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Observações Clínicas (vão para o laboratório)" value={orderForm.clinical_notes} onChange={(e) => setOrderForm({ ...orderForm, clinical_notes: e.target.value })}
                  fullWidth multiline rows={4} placeholder="Informações clínicas relevantes para o laboratório..." />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Observações do Laboratório" value={orderForm.lab_notes} onChange={(e) => setOrderForm({ ...orderForm, lab_notes: e.target.value })}
                  fullWidth multiline rows={3} placeholder="Observações retornadas pelo laboratório..." />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Notas Internas (não vão para o laboratório)" value={orderForm.internal_notes} onChange={(e) => setOrderForm({ ...orderForm, internal_notes: e.target.value })}
                  fullWidth multiline rows={3} placeholder="Notas internas da clínica..." />
              </Grid>
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveOrder} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, minWidth: 120 }}>
            {saving ? <CircularProgress size={20} /> : editingId ? 'Atualizar' : 'Criar Ordem'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* VIEW DIALOG */}
      {/* ========================= */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Detalhes da Ordem {selectedOrder?.order_number}</Typography>
          <IconButton onClick={() => setViewDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Paciente</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.patient_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Laboratório</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedOrder.laboratory_name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Status</Typography>
                  <Box><Chip label={statusLabels[selectedOrder.status]} size="small" sx={{ bgcolor: `${statusColors[selectedOrder.status]}20`, color: statusColors[selectedOrder.status], fontWeight: 600 }} /></Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Prioridade</Typography>
                  <Box><Chip label={priorityLabels[selectedOrder.priority]} size="small" sx={{ bgcolor: `${priorityColors[selectedOrder.priority]}20`, color: priorityColors[selectedOrder.priority], fontWeight: 600 }} /></Box>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>Pagamento</Typography>
                  <Box><Chip label={paymentStatusLabels[selectedOrder.payment_status]} size="small" sx={{ bgcolor: `${paymentStatusColors[selectedOrder.payment_status]}20`, color: paymentStatusColors[selectedOrder.payment_status], fontWeight: 600 }} /></Box>
                </Grid>
                {selectedOrder.shade && (<Grid item xs={4}><Typography variant="caption" sx={{ color: '#64748b' }}>Cor</Typography><Typography sx={{ fontWeight: 600 }}>{selectedOrder.shade} {selectedOrder.shade_guide && `(${selectedOrder.shade_guide})`}</Typography></Grid>)}
                {selectedOrder.tooth_notation && (<Grid item xs={4}><Typography variant="caption" sx={{ color: '#64748b' }}>Dentes</Typography><Typography sx={{ fontWeight: 600 }}>{selectedOrder.tooth_notation}</Typography></Grid>)}
                {selectedOrder.mold_type && (<Grid item xs={4}><Typography variant="caption" sx={{ color: '#64748b' }}>Moldagem</Typography><Typography sx={{ fontWeight: 600 }}>{selectedOrder.mold_type}</Typography></Grid>)}
              </Grid>

              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488', mb: 1 }}>Peças</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead><TableRow sx={{ bgcolor: '#f0fdf4' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Trabalho</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Dente</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Material</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Cor</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                      </TableRow></TableHead>
                      <TableBody>
                        {selectedOrder.items.map((item: any, i: number) => (
                          <TableRow key={i}>
                            <TableCell>{item.work_type_name}</TableCell>
                            <TableCell>{item.tooth_number || '-'}</TableCell>
                            <TableCell>{item.material || '-'}</TableCell>
                            <TableCell>{item.shade || '-'}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{formatCurrency(item.total_price)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={3}><Typography variant="caption" sx={{ color: '#64748b' }}>Subtotal</Typography><Typography sx={{ fontWeight: 600 }}>{formatCurrency(selectedOrder.subtotal)}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" sx={{ color: '#64748b' }}>Desconto</Typography><Typography sx={{ fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(selectedOrder.discount_amount)}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" sx={{ color: '#64748b' }}>Frete</Typography><Typography sx={{ fontWeight: 600 }}>{formatCurrency(selectedOrder.shipping_cost)}</Typography></Grid>
                  <Grid item xs={3}><Typography variant="caption" sx={{ color: '#64748b' }}>Total</Typography><Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(selectedOrder.total_cost)}</Typography></Grid>
                </Grid>
              </Box>

              {selectedOrder.clinical_notes && (<Box sx={{ mt: 2 }}><Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Observações Clínicas</Typography><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{selectedOrder.clinical_notes}</Typography></Box>)}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ========================= */}
      {/* PRINT DIALOG */}
      {/* ========================= */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Imprimir Ordem de Serviço</Typography>
          <Box>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={executePrint}
              sx={{ mr: 1, bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }}>Imprimir</Button>
            <IconButton onClick={() => setPrintDialogOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <div ref={printRef}>
            {selectedOrder && (
              <div>
                <div className="header">
                  <div>
                    <div className="clinic-name">{selectedOrder.clinic?.clinic_name || selectedOrder.order?.clinic_info?.clinic_name || 'Clínica'}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>
                      {selectedOrder.clinic?.phone && <span>Tel: {selectedOrder.clinic.phone} | </span>}
                      {selectedOrder.clinic?.email && <span>{selectedOrder.clinic.email}</span>}
                    </div>
                    {selectedOrder.clinic?.address_street && (
                      <div style={{ fontSize: '10px', color: '#64748b' }}>
                        {selectedOrder.clinic.address_street}, {selectedOrder.clinic.address_number} - {selectedOrder.clinic.address_neighborhood} - {selectedOrder.clinic.address_city}/{selectedOrder.clinic.address_state}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#0d9488' }}>ORDEM DE SERVIÇO</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{(selectedOrder.order || selectedOrder).order_number}</div>
                    <div style={{ fontSize: '10px', color: '#64748b' }}>Data: {formatDate((selectedOrder.order || selectedOrder).order_date)}</div>
                    <div className="priority-badge" style={{ backgroundColor: `${priorityColors[(selectedOrder.order || selectedOrder).priority]}20`, color: priorityColors[(selectedOrder.order || selectedOrder).priority] }}>
                      {priorityLabels[(selectedOrder.order || selectedOrder).priority]}
                    </div>
                  </div>
                </div>

                <div className="section">
                  <div className="section-title">LABORATÓRIO</div>
                  <div className="row"><span className="label">Nome:</span> {(selectedOrder.order || selectedOrder).laboratory_name}</div>
                  {(selectedOrder.order || selectedOrder).laboratory_phone && <div className="row"><span className="label">Telefone:</span> {(selectedOrder.order || selectedOrder).laboratory_phone}</div>}
                </div>

                <div className="section">
                  <div className="section-title">PACIENTE</div>
                  <div className="row"><span className="label">Nome:</span> {(selectedOrder.order || selectedOrder).patient_name}</div>
                  {(selectedOrder.order || selectedOrder).patient_age_text && <div className="row"><span className="label">Idade:</span> {(selectedOrder.order || selectedOrder).patient_age_text}</div>}
                  {(selectedOrder.order || selectedOrder).patient_gender && <div className="row"><span className="label">Gênero:</span> {(selectedOrder.order || selectedOrder).patient_gender}</div>}
                </div>

                {(selectedOrder.order || selectedOrder).professional_name && (
                  <div className="section">
                    <div className="section-title">PROFISSIONAL SOLICITANTE</div>
                    <div className="row"><span className="label">Dr(a):</span> {(selectedOrder.order || selectedOrder).professional_name} {(selectedOrder.order || selectedOrder).professional_registration && `- CRO ${(selectedOrder.order || selectedOrder).professional_registration}`}</div>
                  </div>
                )}

                <div className="section">
                  <div className="section-title">INFORMAÇÕES CLÍNICAS</div>
                  {(selectedOrder.order || selectedOrder).tooth_notation && <div className="row"><span className="label">Dentes:</span> {(selectedOrder.order || selectedOrder).tooth_notation}</div>}
                  {(selectedOrder.order || selectedOrder).shade && <div className="row"><span className="label">Cor:</span> {(selectedOrder.order || selectedOrder).shade} {(selectedOrder.order || selectedOrder).shade_guide && `(${(selectedOrder.order || selectedOrder).shade_guide})`}</div>}
                  {(selectedOrder.order || selectedOrder).shade_details && <div className="row"><span className="label">Detalhes de Cor:</span> {(selectedOrder.order || selectedOrder).shade_details}</div>}
                  {(selectedOrder.order || selectedOrder).dental_arch && <div className="row"><span className="label">Arcada:</span> {(selectedOrder.order || selectedOrder).dental_arch}</div>}
                  {(selectedOrder.order || selectedOrder).mold_type && <div className="row"><span className="label">Moldagem:</span> {(selectedOrder.order || selectedOrder).mold_type}</div>}
                  {(selectedOrder.order || selectedOrder).diagnosis && <div className="row"><span className="label">Diagnóstico:</span> {(selectedOrder.order || selectedOrder).diagnosis}</div>}
                </div>

                <div className="section">
                  <div className="section-title">PEÇAS / TRABALHOS</div>
                  <table>
                    <thead><tr><th>#</th><th>Trabalho</th><th>Dente</th><th>Material</th><th>Cor</th><th>Qtd</th><th>Valor</th></tr></thead>
                    <tbody>
                      {((selectedOrder.order || selectedOrder).items || []).map((item: any, i: number) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{item.work_type_name}</td>
                          <td>{item.tooth_number || '-'}</td>
                          <td>{item.material || '-'}</td>
                          <td>{item.shade || '-'}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.total_price)}</td>
                        </tr>
                      ))}
                      <tr className="total-row">
                        <td colSpan={6} style={{ textAlign: 'right' }}>TOTAL:</td>
                        <td>{formatCurrency((selectedOrder.order || selectedOrder).total_cost)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {(selectedOrder.order || selectedOrder).clinical_notes && (
                  <div className="section">
                    <div className="section-title">OBSERVAÇÕES PARA O LABORATÓRIO</div>
                    <div className="notes">{(selectedOrder.order || selectedOrder).clinical_notes}</div>
                  </div>
                )}

                <div className="section" style={{ marginTop: '15px' }}>
                  <div className="section-title">PRAZO</div>
                  <div className="row"><span className="label">Envio:</span> {formatDate((selectedOrder.order || selectedOrder).sent_date || (selectedOrder.order || selectedOrder).order_date)}</div>
                  <div className="row"><span className="label">Prazo Retorno:</span> {formatDate((selectedOrder.order || selectedOrder).expected_return_date)}</div>
                </div>

                <div className="signatures">
                  <div className="signature-line">
                    {(() => {
                      const o = selectedOrder.order || selectedOrder;
                      const sig = String(o?.professional_signature_image_url ?? '').trim();
                      return sig ? (
                        <div className="prof-sig-print"><img src={sig} alt="" /></div>
                      ) : null;
                    })()}
                    <hr /><span>Clínica / Profissional</span></div>
                  <div className="signature-line"><hr /><span>Laboratório</span></div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ========================= */}
      {/* STATUS CHANGE DIALOG */}
      {/* ========================= */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4' }}>Alterar Status - {menuOrder?.order_number}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Novo Status</InputLabel>
            <Select value={statusChangeData.status} onChange={(e) => setStatusChangeData({ ...statusChangeData, status: e.target.value })} label="Novo Status">
              {Object.entries(statusLabels).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Observações" value={statusChangeData.notes} onChange={(e) => setStatusChangeData({ ...statusChangeData, notes: e.target.value })} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleStatusChange} sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }}>Confirmar</Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* PAYMENT DIALOG */}
      {/* ========================= */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4' }}>Registrar Pagamento - {menuOrder?.order_number}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField label="Valor (R$)" type="number" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Data" type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Forma de Pagamento" value={paymentForm.payment_method} onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })} fullWidth placeholder="PIX, Boleto, Transferência..." />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Referência (NF, comprovante)" value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Descrição" value={paymentForm.description} onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })} fullWidth multiline rows={2} />
            </Grid>
            {menuOrder && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
                  Total da OS: {formatCurrency(menuOrder.total_cost)} | Pago: {formatCurrency(menuOrder.paid_amount)} | Restante: {formatCurrency((menuOrder.total_cost || 0) - (menuOrder.paid_amount || 0))}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleAddPayment} sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }}>Registrar Pagamento</Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* HISTORY DIALOG */}
      {/* ========================= */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4' }}>Histórico de Status</DialogTitle>
        <DialogContent dividers>
          {orderHistory.length === 0 ? (
            <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 2 }}>Nenhum registro</Typography>
          ) : orderHistory.map((h, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
              <TimeIcon sx={{ color: '#0d9488', mt: 0.3 }} />
              <Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  {h.previous_status && <Chip label={statusLabels[h.previous_status] || h.previous_status} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />}
                  {h.previous_status && <span>→</span>}
                  <Chip label={statusLabels[h.new_status] || h.new_status} size="small" sx={{ fontSize: '0.6rem', height: 20, bgcolor: `${statusColors[h.new_status]}20`, color: statusColors[h.new_status], fontWeight: 600 }} />
                </Box>
                {h.notes && <Typography variant="caption" sx={{ color: '#475569' }}>{h.notes}</Typography>}
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>
                  {new Date(h.created_at).toLocaleString('pt-BR')}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* ========================= */}
      {/* LABORATORY CREATE/EDIT DIALOG */}
      {/* ========================= */}
      <Dialog open={labDialogOpen} onClose={() => setLabDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingLabId ? 'Editar Laboratório' : 'Novo Laboratório'}</Typography>
          <IconButton onClick={() => setLabDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Dados do Laboratório</Typography></Grid>
            <Grid item xs={6}><TextField label="Nome *" value={labForm.name} onChange={(e) => setLabForm({ ...labForm, name: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={6}><TextField label="Nome Fantasia" value={labForm.trade_name} onChange={(e) => setLabForm({ ...labForm, trade_name: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="CNPJ" value={labForm.cnpj} onChange={(e) => setLabForm({ ...labForm, cnpj: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Registro/Alvará" value={labForm.registration_number} onChange={(e) => setLabForm({ ...labForm, registration_number: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Prazo Padrão (dias)" type="number" value={labForm.default_turnaround_days} onChange={(e) => setLabForm({ ...labForm, default_turnaround_days: parseInt(e.target.value) || 7 })} fullWidth size="small" /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Responsável Técnico (TPD)</Typography></Grid>
            <Grid item xs={6}><TextField label="Nome do Responsável" value={labForm.responsible_name} onChange={(e) => setLabForm({ ...labForm, responsible_name: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={6}><TextField label="CRO / Registro" value={labForm.responsible_registration} onChange={(e) => setLabForm({ ...labForm, responsible_registration: e.target.value })} fullWidth size="small" /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Contato</Typography></Grid>
            <Grid item xs={4}><TextField label="Telefone" value={labForm.phone} onChange={(e) => setLabForm({ ...labForm, phone: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Telefone 2" value={labForm.phone2} onChange={(e) => setLabForm({ ...labForm, phone2: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="WhatsApp" value={labForm.whatsapp} onChange={(e) => setLabForm({ ...labForm, whatsapp: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={6}><TextField label="Email" value={labForm.email} onChange={(e) => setLabForm({ ...labForm, email: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={6}><TextField label="Website" value={labForm.website} onChange={(e) => setLabForm({ ...labForm, website: e.target.value })} fullWidth size="small" /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Endereço</Typography></Grid>
            <Grid item xs={6}><TextField label="Rua" value={labForm.address_street} onChange={(e) => setLabForm({ ...labForm, address_street: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={2}><TextField label="Número" value={labForm.address_number} onChange={(e) => setLabForm({ ...labForm, address_number: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Complemento" value={labForm.address_complement} onChange={(e) => setLabForm({ ...labForm, address_complement: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Bairro" value={labForm.address_neighborhood} onChange={(e) => setLabForm({ ...labForm, address_neighborhood: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Cidade" value={labForm.address_city} onChange={(e) => setLabForm({ ...labForm, address_city: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={2}><TextField label="UF" value={labForm.address_state} onChange={(e) => setLabForm({ ...labForm, address_state: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={2}><TextField label="CEP" value={labForm.address_zip} onChange={(e) => setLabForm({ ...labForm, address_zip: e.target.value })} fullWidth size="small" /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Especialidades e Materiais</Typography></Grid>
            <Grid item xs={6}>
              <Autocomplete multiple freeSolo options={['Prótese Fixa', 'Prótese Removível', 'Prótese sobre Implante', 'Ortodontia', 'Clareamento', 'Modelos', 'Digital']}
                value={labForm.specialties} onChange={(_, v) => setLabForm({ ...labForm, specialties: v })}
                renderInput={(params) => <TextField {...params} label="Especialidades" size="small" />} />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete multiple freeSolo options={['Zircônia', 'Dissilicato de Lítio', 'Metalocerâmica', 'Porcelana', 'Resina', 'Metal', 'Silicone', 'Impressão 3D', 'CAD/CAM']}
                value={labForm.materials_offered} onChange={(_, v) => setLabForm({ ...labForm, materials_offered: v })}
                renderInput={(params) => <TextField {...params} label="Materiais Oferecidos" size="small" />} />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0d9488' }}>Dados Bancários / Pagamento</Typography></Grid>
            <Grid item xs={4}><TextField label="Condições de Pagamento" value={labForm.payment_terms} onChange={(e) => setLabForm({ ...labForm, payment_terms: e.target.value })} fullWidth size="small" placeholder="30 dias, À vista..." /></Grid>
            <Grid item xs={4}><TextField label="Forma de Pagamento" value={labForm.payment_method} onChange={(e) => setLabForm({ ...labForm, payment_method: e.target.value })} fullWidth size="small" placeholder="PIX, Boleto..." /></Grid>
            <Grid item xs={4}><TextField label="Chave PIX" value={labForm.pix_key} onChange={(e) => setLabForm({ ...labForm, pix_key: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Banco" value={labForm.bank_name} onChange={(e) => setLabForm({ ...labForm, bank_name: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Agência" value={labForm.bank_agency} onChange={(e) => setLabForm({ ...labForm, bank_agency: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Conta" value={labForm.bank_account} onChange={(e) => setLabForm({ ...labForm, bank_account: e.target.value })} fullWidth size="small" /></Grid>

            <Grid item xs={12}><TextField label="Observações" value={labForm.notes} onChange={(e) => setLabForm({ ...labForm, notes: e.target.value })} fullWidth multiline rows={3} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setLabDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveLab} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }}>
            {saving ? <CircularProgress size={20} /> : editingLabId ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================= */}
      {/* LAB FINANCIAL DIALOG */}
      {/* ========================= */}
      <Dialog open={labFinancialDialogOpen} onClose={() => setLabFinancialDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4' }}>Financeiro - {labFinancial?.lab?.name}</DialogTitle>
        <DialogContent dividers>
          {labFinancial && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #99f6e4', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Total Gasto</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0d9488' }}>{formatCurrency(labFinancial.total_cost)}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #99f6e4', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Total Pago</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>{formatCurrency(labFinancial.total_paid)}</Typography>
                  </Card>
                </Grid>
                <Grid item xs={4}>
                  <Card sx={{ p: 2, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>A Pagar</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>{formatCurrency(labFinancial.total_pending)}</Typography>
                  </Card>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Ordens: {labFinancial.total_orders} | Concluídas: {labFinancial.completed_orders}</Typography>
              {labFinancial.recent_payments && labFinancial.recent_payments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Últimos Pagamentos</Typography>
                  {labFinancial.recent_payments.map((p: any, i: number) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', p: 1, bgcolor: '#f8fafc', borderRadius: 1, mb: 0.5 }}>
                      <Typography variant="caption">{formatDate(p.payment_date)} - {p.payment_method || 'N/A'}</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#10b981' }}>{formatCurrency(p.amount)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ========================= */}
      {/* WORK TYPE CREATE/EDIT DIALOG */}
      {/* ========================= */}
      <Dialog open={wtDialogOpen} onClose={() => setWtDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f0fdf4', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{editingWtId ? 'Editar Tipo de Trabalho' : 'Novo Tipo de Trabalho'}</Typography>
          <IconButton onClick={() => setWtDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={8}><TextField label="Nome *" value={wtForm.name} onChange={(e) => setWtForm({ ...wtForm, name: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={4}><TextField label="Código" value={wtForm.code} onChange={(e) => setWtForm({ ...wtForm, code: e.target.value })} fullWidth size="small" placeholder="PF-001" /></Grid>
            <Grid item xs={6}>
              <Autocomplete freeSolo options={['Prótese Fixa', 'Prótese Removível', 'Prótese sobre Implante', 'Ortodontia', 'Clareamento', 'Modelos']}
                value={wtForm.category} onChange={(_, v) => setWtForm({ ...wtForm, category: v || '' })}
                onInputChange={(_, v) => setWtForm({ ...wtForm, category: v })}
                renderInput={(params) => <TextField {...params} label="Categoria *" size="small" />} />
            </Grid>
            <Grid item xs={6}><TextField label="Subcategoria" value={wtForm.subcategory} onChange={(e) => setWtForm({ ...wtForm, subcategory: e.target.value })} fullWidth size="small" placeholder="Coroa, Ponte, Faceta..." /></Grid>
            <Grid item xs={6}><TextField label="Material" value={wtForm.material} onChange={(e) => setWtForm({ ...wtForm, material: e.target.value })} fullWidth size="small" /></Grid>
            <Grid item xs={3}><TextField label="Preço (R$)" type="number" value={wtForm.default_price} onChange={(e) => setWtForm({ ...wtForm, default_price: parseFloat(e.target.value) || 0 })} fullWidth size="small" /></Grid>
            <Grid item xs={3}><TextField label="Prazo (dias)" type="number" value={wtForm.default_turnaround_days} onChange={(e) => setWtForm({ ...wtForm, default_turnaround_days: parseInt(e.target.value) || 7 })} fullWidth size="small" /></Grid>
            <Grid item xs={12}><TextField label="Descrição" value={wtForm.description} onChange={(e) => setWtForm({ ...wtForm, description: e.target.value })} fullWidth multiline rows={2} /></Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Requisitos</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel control={<Switch checked={wtForm.requires_shade} onChange={(e) => setWtForm({ ...wtForm, requires_shade: e.target.checked })} color="primary" />} label="Cor/Escala" />
                <FormControlLabel control={<Switch checked={wtForm.requires_mold} onChange={(e) => setWtForm({ ...wtForm, requires_mold: e.target.checked })} color="primary" />} label="Moldagem" />
                <FormControlLabel control={<Switch checked={wtForm.requires_photo} onChange={(e) => setWtForm({ ...wtForm, requires_photo: e.target.checked })} color="primary" />} label="Fotografia" />
                <FormControlLabel control={<Switch checked={wtForm.requires_scan} onChange={(e) => setWtForm({ ...wtForm, requires_scan: e.target.checked })} color="primary" />} label="Scan Digital" />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={() => setWtDialogOpen(false)} sx={{ color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveWt} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' } }}>
            {saving ? <CircularProgress size={20} /> : editingWtId ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Laboratory;
